import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Stack, Chip, Button, Divider, Alert, CircularProgress,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, AccessTimeOutlined, HighlightOff, DownloadOutlined,
} from '@mui/icons-material';
import { C } from '../vps/theme';
import { BASE_URL, API_ENDPOINTS } from '../../config/api.config';
import { generateInvoicePdf } from '../../utils/generateInvoicePdf';

// "YYYY-MM-DD HH:mm:ss" -> "06 Aug 2026"
const formatDate = (str) => {
  if (!str) return '-';
  const [datePart] = str.split(' ');
  const [y, m, d] = (datePart || '').split('-').map(Number);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return (y && m && d) ? `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}` : '-';
};

const formatAmount = (amount, currency = 'INR') => {
  const num = parseFloat(amount) || 0;
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getStatusMeta = (payment) => {
  const status = (payment.payment_status || '').toLowerCase();
  if (status === 'success') {
    return { Icon: CheckCircle, color: C.success, bg: C.successLight };
  }
  if (status === 'pending') {
    return { Icon: AccessTimeOutlined, color: C.warning, bg: C.warningLight };
  }
  return { Icon: HighlightOff, color: '#DC2626', bg: '#FEF2F2' };
};

const PaymentsPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (payment) => {
    const key = payment.id || payment.order_id;
    setDownloadingId(key);
    try {
      await generateInvoicePdf(payment);
    } catch (err) {
      console.error('[Invoice PDF] Failed to generate:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    let ignore = false;

    const fetchPayments = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${BASE_URL}${API_ENDPOINTS.GET_PAYMENTS}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const result = await res.json();
        if (!res.ok || result.responseCode !== '200') {
          throw new Error(result.responseMessage || 'Could not load your payment history.');
        }
        if (!ignore) setPayments(Array.isArray(result.data) ? result.data : []);
      } catch (err) {
        if (!ignore) setError(err.message || 'Could not load your payment history. Please try again.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchPayments();
    return () => { ignore = true; };
  }, []);

  return (
    <Box sx={{ background: C.bgSection, minHeight: '90vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Back Link */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: C.primary, fontWeight: 600, textTransform: 'none', mb: 2, '&:hover': { background: C.primaryLight } }}
          >
            Back to Home
          </Button>

          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, color: C.textPrimary, mb: 1 }}>
            Payment History & Invoices
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '0.98rem' }}>
            View your transactions and download invoices for every VPS order.
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: C.primary }} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius: '10px', fontWeight: 600 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && payments.length === 0 && (
          <Paper elevation={0} sx={{ p: 5, borderRadius: '16px', textAlign: 'center', border: `1px solid ${C.border}`, background: '#FFFFFF' }}>
            <Typography sx={{ color: C.textSecondary, fontWeight: 600 }}>
              No payments yet. Once you place an order, your invoices will show up here.
            </Typography>
          </Paper>
        )}

        {!loading && !error && payments.length > 0 && (
          <Stack spacing={2.5}>
            {payments.map((payment) => {
              const meta = getStatusMeta(payment);
              return (
                <Paper elevation={0} key={payment.id || payment.order_id} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: '16px', background: '#FFFFFF', border: `1px solid ${C.border}` }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: C.textPrimary }}>
                          {payment.plan_name || 'VPS Hosting Plan'}
                        </Typography>
                        <Chip
                          icon={<meta.Icon sx={{ fontSize: '15px !important', color: `${meta.color} !important` }} />}
                          label={payment.payment_status || '-'}
                          size="small"
                          sx={{ background: meta.bg, color: meta.color, fontWeight: 800 }}
                        />
                      </Stack>
                      <Typography variant="caption" sx={{ color: C.textMuted }}>
                        Order ID: <strong>{payment.order_id}</strong> · {formatDate(payment.created_date)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: { sm: 'right' }, flexShrink: 0 }}>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: C.primary, fontFamily: 'monospace' }}>
                        {formatAmount(payment.amount, payment.currency)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ borderColor: C.border, my: 2 }} />

                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                    <Typography variant="caption" sx={{ color: C.textSecondary, fontWeight: 600 }}>
                      Transaction ID: <strong style={{ fontFamily: 'monospace', color: C.primary }}>{payment.cashfree_order_id || '-'}</strong>
                    </Typography>
                    <Button
                      startIcon={downloadingId === (payment.id || payment.order_id) ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlined />}
                      size="small"
                      variant="outlined"
                      disabled={downloadingId === (payment.id || payment.order_id)}
                      onClick={() => handleDownload(payment)}
                      sx={{ borderColor: C.border, color: C.textPrimary, textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { borderColor: C.primary, color: C.primary, background: C.primaryLight } }}
                    >
                      {downloadingId === (payment.id || payment.order_id) ? 'Preparing PDF...' : 'Download Invoice PDF'}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default PaymentsPage;
